import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { getTractorDoubleBookingMessage } from "@/lib/order-conflict-errors";
import { useTractorConflictIds } from "@/hooks/useTractorConflictIds";
import {
  useTractorOosStatusMap,
  computeOosBlockedIdsForWindow,
} from "@/hooks/useTractorOosStatusMap";

type AssignKind = "tractor" | "operator";

interface InlineAssignCellProps {
  orderId: string;
  kind: AssignKind;
  /** Current assigned id (or null when unassigned). */
  currentId: string | null;
  /** Current assigned display name (used for read-only render and audit log). */
  currentName: string | null;
  /** When true, empty state renders an em dash instead of "+ Add". */
  readOnly?: boolean;
}

/**
 * Inline assignment cell for the Orders table — mirrors the
 * Tender ID / Trailer ID / Trailer Type "+ Add" UX.
 *
 * - When a value is set: renders the assigned name as plain text.
 * - When empty: renders a "+ Add" button that opens a searchable
 *   combobox of tractors/drivers and writes the selection straight
 *   to `orders.assigned_tractor_id` / `orders.assigned_operator_id`,
 *   plus an `order_change_log` audit entry — same pattern as
 *   `InlineTrailerTypeCell`.
 *
 * Notes:
 * - We deliberately do NOT route through `useOrderMutations.updateMutation`.
 *   Inline tractor/operator assignment is a simple field write — promotion
 *   to `confirmed` is always an explicit manual action by dispatch via the
 *   Confirm Order button or status field, never implicit on assignment.
 * - Tenant isolation is enforced by RLS on `tractors`/`drivers`.
 */
export function InlineAssignCell({
  orderId,
  kind,
  currentId,
  currentName,
  readOnly = false,
}: InlineAssignCellProps) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const isEmpty = !currentId;

  // Read-only render when assigned — keeps parity with existing
  // Tractor/Operator cells.
  if (!isEmpty) {
    return <span className="text-sm">{currentName || "—"}</span>;
  }

  if (readOnly) {
    return <span className="text-sm">—</span>;
  }

  return (
    <InlineAssignPopover
      kind={kind}
      orderId={orderId}
      open={open}
      setOpen={setOpen}
      isSaving={isSaving}
      onSelect={async (option) => {
        setIsSaving(true);
        try {
          const fieldName = kind === "tractor"
            ? "assigned_tractor_id"
            : "assigned_operator_id";

          const { error } = await supabase
            .from("orders")
            .update({ [fieldName]: option.id })
            .eq("id", orderId);
          if (error) throw error;

          await supabase.from("order_change_log").insert({
            order_id: orderId,
            field_name: kind === "tractor" ? "assigned_tractor" : "assigned_operator",
            old_value: null,
            new_value: option.name,
            changed_by: user?.email || "Unknown",
          });

          queryClient.invalidateQueries({ queryKey: ["orders"] });
          queryClient.invalidateQueries({ queryKey: ["starred-orders"] });
          queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
          queryClient.invalidateQueries({ queryKey: ["tractor-conflict-ids"] });
          toast.success(
            kind === "tractor" ? "Tractor assigned" : "Operator assigned",
          );
          setOpen(false);
        } catch (err) {
          console.error("Inline assign error:", err);
          toast.error(getTractorDoubleBookingMessage(err) ?? "Failed to assign");
        } finally {
          setIsSaving(false);
        }
      }}
    />
  );
}

interface InlineAssignPopoverProps {
  kind: AssignKind;
  orderId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  isSaving: boolean;
  onSelect: (option: { id: string; name: string }) => void;
}

function InlineAssignPopover({
  kind,
  orderId,
  open,
  setOpen,
  isSaving,
  onSelect,
}: InlineAssignPopoverProps) {
  // Defer the option fetch until the popover is opened to avoid loading
  // every tractor/driver on every Orders table render.
  const { data: options, isLoading } = useQuery({
    queryKey: [kind === "tractor" ? "inline-tractors" : "inline-drivers"],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(kind === "tractor" ? "tractors" : "drivers")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; name: string }>;
    },
  });

  const { data: orderWindow } = useQuery({
    queryKey: ["inline-assignment-order-window", orderId],
    enabled: open && kind === "tractor",
    queryFn: async () => {
      const { data: order, error: orderErr } = await supabase
        .from("orders")
        .select(
          "tenant_id, ready_time, requested_delivery_time, requested_delivery_time_end",
        )
        .eq("id", orderId)
        .maybeSingle();
      if (orderErr) throw orderErr;
      return {
        tenantId: order?.tenant_id as string | null,
        start: order?.ready_time as string | null,
        end: (order?.requested_delivery_time_end || order?.requested_delivery_time) as string | null,
      };
    },
  });

  const { data: conflictingTractorIds } = useTractorConflictIds({
    tenantId: orderWindow?.tenantId,
    start: orderWindow?.start,
    end: orderWindow?.end,
    enabled: open && kind === "tractor",
    excludeOrderId: orderId,
    excludeLegOrderId: orderId,
  });

  // OOS awareness (tractor pickers only).
  const { statusById: oosStatusById, recordsById: oosRecordsById } =
    useTractorOosStatusMap({ enabled: open && kind === "tractor" });
  const oosBlockedIds =
    kind === "tractor"
      ? computeOosBlockedIdsForWindow(
          oosRecordsById,
          orderWindow?.start,
          orderWindow?.end,
        )
      : new Set<string>();

  const placeholder = kind === "tractor" ? "Search tractors..." : "Search drivers...";
  const emptyText = kind === "tractor" ? "No tractors found." : "No drivers found.";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-sm text-muted-foreground hover:text-foreground hover:underline cursor-pointer disabled:opacity-50"
          disabled={isSaving}
        >
          + Add
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[240px] p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList className="max-h-[260px] overflow-y-auto">
            <CommandEmpty>
              {isLoading ? "Loading…" : emptyText}
            </CommandEmpty>
            <CommandGroup>
              {options?.map((opt) => {
                const hasConflict =
                  kind === "tractor" && !!conflictingTractorIds?.has(opt.id);
                const oosStatus =
                  kind === "tractor" ? oosStatusById.get(opt.id) : undefined;
                const isOosBlocking =
                  kind === "tractor" && oosBlockedIds.has(opt.id);
                const isUpcoming = oosStatus === "upcoming_service";
                const isBlocked = hasConflict || isOosBlocking;
                const badgeLabel = hasConflict
                  ? "booked"
                  : isOosBlocking
                    ? "OOS"
                    : isUpcoming
                      ? "upcoming service"
                      : null;
                return (
                  <CommandItem
                    key={opt.id}
                    value={opt.name}
                    disabled={isSaving || isBlocked}
                    aria-disabled={isSaving || isBlocked}
                    className={cn(
                      isBlocked && "pointer-events-none cursor-not-allowed bg-muted text-muted-foreground opacity-60 data-[selected=true]:bg-muted data-[selected=true]:text-muted-foreground",
                    )}
                    onSelect={() => {
                      if (isBlocked) return;
                      onSelect(opt);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4 opacity-0")} />
                    <span className="flex-1">{opt.name}</span>
                    {badgeLabel && (
                      <span
                        className={cn(
                          "ml-2 text-xs",
                          isOosBlocking ? "text-destructive" : "text-muted-foreground",
                        )}
                      >
                        {badgeLabel}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
